import React from 'react';
import scribaneMarkUrl from '../assets/logos/SVG/POSITIVO/logo scribane_positivo micro simple adjusted.svg';

export const EMPTY_ILLUSTRATION_FLOAT_STYLE = {
  a: { animation: 'empty-illustration-float-a 3.2s ease-in-out infinite' },
  b: { animation: 'empty-illustration-float-b 3.6s ease-in-out infinite 0.3s' },
  c: { animation: 'empty-illustration-float-c 3.4s ease-in-out infinite 0.6s' },
} as const;

export type EmptyIllustrationFloatVariant = keyof typeof EMPTY_ILLUSTRATION_FLOAT_STYLE;

export const emptyIllustrationSvgFloatStyle = (
  variant: EmptyIllustrationFloatVariant
): React.CSSProperties => ({
  ...EMPTY_ILLUSTRATION_FLOAT_STYLE[variant],
  transformBox: 'fill-box',
  transformOrigin: 'center',
});

export type EmptyIllustrationSize = 'default' | 'sm';

const EMPTY_ILLUSTRATION_FRAME_CLASSNAME: Record<EmptyIllustrationSize, string> = {
  default: 'relative mx-auto mb-8 h-44 w-60',
  sm: 'relative mx-auto mb-3 h-32 w-48',
};

export const EmptyIllustrationFrame: React.FC<{
  children: React.ReactNode;
  size?: EmptyIllustrationSize;
}> = ({ children, size = 'default' }) => {
  return (
    <div className={EMPTY_ILLUSTRATION_FRAME_CLASSNAME[size]} aria-hidden="true">
      <style>{`
        @keyframes empty-illustration-float-a { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes empty-illustration-float-b { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-11px); } }
        @keyframes empty-illustration-float-c { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
      `}</style>
      {children}
    </div>
  );
};

export const EmptyIllustrationSvg: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <svg viewBox="0 0 240 176" className="absolute inset-0 h-full w-full" fill="none">
      {children}
    </svg>
  );
};

export const EmptyIllustrationGroundShadow: React.FC<{ rx?: number }> = ({ rx = 46 }) => {
  return <ellipse cx="120" cy="164" rx={rx} ry="6" fill="var(--muted)" />;
};

export type EmptyIllustrationCardTone = 'filled' | 'outline-green' | 'muted';

interface EmptyIllustrationMiniCardProps {
  x: number;
  y: number;
  variant: EmptyIllustrationFloatVariant;
  tone: EmptyIllustrationCardTone;
  width?: number;
  height?: number;
}

export const EmptyIllustrationMiniCard: React.FC<EmptyIllustrationMiniCardProps> = ({
  x,
  y,
  variant,
  tone,
  width = 32,
  height = 24,
}) => {
  const isFilled = tone === 'filled';
  const isGreenOutline = tone === 'outline-green';
  const lineStroke = isFilled ? 'white' : isGreenOutline ? 'var(--scribane-green)' : 'var(--muted-foreground)';

  return (
    <g style={emptyIllustrationSvgFloatStyle(variant)}>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx="5"
        fill={isFilled ? 'var(--scribane-green)' : 'var(--card)'}
        stroke={isFilled ? undefined : isGreenOutline ? 'var(--scribane-green)' : 'var(--border)'}
        strokeWidth={isFilled ? undefined : 2}
      />
      <line
        x1={x + 7}
        y1={y + 9}
        x2={x + width - 7}
        y2={y + 9}
        stroke={lineStroke}
        strokeWidth="2"
        strokeLinecap="round"
        opacity={isFilled ? 0.9 : isGreenOutline ? 0.8 : 0.7}
      />
      <line
        x1={x + 7}
        y1={y + 15}
        x2={x + width - 13}
        y2={y + 15}
        stroke={lineStroke}
        strokeWidth="2"
        strokeLinecap="round"
        opacity={isFilled ? 0.7 : isGreenOutline ? 0.6 : 0.5}
      />
    </g>
  );
};

interface EmptyIllustrationAvatarProps {
  cx: number;
  cy: number;
  variant: EmptyIllustrationFloatVariant;
  tone: EmptyIllustrationCardTone;
}

export const EmptyIllustrationAvatar: React.FC<EmptyIllustrationAvatarProps> = ({
  cx,
  cy,
  variant,
  tone,
}) => {
  const isFilled = tone === 'filled';
  const isGreenOutline = tone === 'outline-green';
  const accent = isFilled ? 'white' : isGreenOutline ? 'var(--scribane-green)' : 'var(--muted-foreground)';

  return (
    <g style={emptyIllustrationSvgFloatStyle(variant)}>
      <circle
        cx={cx}
        cy={cy}
        r="18"
        fill={isFilled ? 'var(--scribane-green)' : 'var(--card)'}
        stroke={isFilled ? undefined : isGreenOutline ? 'var(--scribane-green)' : 'var(--border)'}
        strokeWidth={isFilled ? undefined : 2}
      />
      <circle cx={cx} cy={cy - 4} r="6" fill={accent} opacity={isFilled ? 0.95 : 0.8} />
      <path
        d={`M${cx - 10} ${cy + 12} C ${cx - 8} ${cy + 4}, ${cx + 8} ${cy + 4}, ${cx + 10} ${cy + 12}`}
        fill={accent}
        opacity={isFilled ? 0.85 : 0.65}
      />
    </g>
  );
};

export const NotesEmptyIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={50} />
        <rect x="78" y="118" width="84" height="42" rx="6" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
        <line x1="90" y1="132" x2="150" y2="132" stroke="var(--border)" strokeWidth="1.5" opacity="0.7" />
        <line x1="90" y1="140" x2="142" y2="140" stroke="var(--border)" strokeWidth="1.5" opacity="0.55" />
        <line x1="90" y1="148" x2="134" y2="148" stroke="var(--border)" strokeWidth="1.5" opacity="0.4" />
        <EmptyIllustrationMiniCard x={32} y={48} variant="a" tone="muted" />
        <EmptyIllustrationMiniCard x={104} y={18} variant="b" tone="filled" width={34} />
        <EmptyIllustrationMiniCard x={174} y={42} variant="c" tone="outline-green" />
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};

export const ProjectsEmptyIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={54} />
        <rect x="86" y="124" width="92" height="32" rx="6" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
        <rect x="78" y="116" width="92" height="32" rx="6" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
        <rect x="70" y="104" width="100" height="40" rx="8" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
        <rect x="80" y="114" width="12" height="12" rx="3" fill="var(--scribane-green)" />
        <line x1="98" y1="120" x2="154" y2="120" stroke="var(--border)" strokeWidth="1.5" opacity="0.7" />
        <line x1="98" y1="128" x2="142" y2="128" stroke="var(--border)" strokeWidth="1.5" opacity="0.5" />
        <EmptyIllustrationMiniCard x={28} y={42} variant="a" tone="muted" />
        <EmptyIllustrationMiniCard x={104} y={14} variant="b" tone="filled" width={34} />
        <EmptyIllustrationMiniCard x={176} y={38} variant="c" tone="outline-green" />
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};

const NOTES_SHORTCUT_ANIMATION_STYLE: React.CSSProperties = {
  transformBox: 'fill-box',
  transformOrigin: 'center',
};

export const NotesPwaShortcutIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame size="sm">
      <style>{`
        @keyframes notes-shortcut-press {
          0%, 12% { transform: scale(1); }
          22%, 68% { transform: scale(0.9); }
          80%, 100% { transform: scale(1); }
        }
        @keyframes notes-shortcut-ripple {
          0%, 14% { opacity: 0; transform: scale(0.55); }
          24% { opacity: 0.45; }
          52% { opacity: 0; transform: scale(1.75); }
          100% { opacity: 0; transform: scale(1.75); }
        }
        @keyframes notes-shortcut-touch {
          0%, 8% { opacity: 0; transform: scale(0.5); }
          18%, 66% { opacity: 0.9; transform: scale(1); }
          78%, 100% { opacity: 0; transform: scale(0.5); }
        }
        @keyframes notes-shortcut-finger {
          0%, 4% { opacity: 0.35; transform: translate(16px, 42px) rotate(18deg); }
          12% { opacity: 1; transform: translate(5px, 14px) rotate(7deg); }
          18% { opacity: 1; transform: translate(0, 3px) rotate(2deg); }
          22%, 66% { opacity: 1; transform: translate(0, 6px) rotate(1deg) scale(0.96); }
          76% { opacity: 1; transform: translate(7px, 18px) rotate(9deg); }
          88%, 100% { opacity: 0.35; transform: translate(16px, 42px) rotate(18deg); }
        }
        @keyframes notes-shortcut-menu {
          0%, 26% { opacity: 0; transform: translateY(10px) scale(0.9); }
          38%, 78% { opacity: 1; transform: translateY(0) scale(1); }
          90%, 100% { opacity: 0; transform: translateY(-4px) scale(0.96); }
        }
        @keyframes notes-shortcut-item {
          0%, 40% { opacity: 0.55; }
          50%, 74% { opacity: 1; }
          88%, 100% { opacity: 0.55; }
        }
      `}</style>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={52} />
        <g
          style={{
            ...NOTES_SHORTCUT_ANIMATION_STYLE,
            animation: 'notes-shortcut-ripple 3.4s ease-out infinite',
          }}
        >
          <circle
            cx="74"
            cy="96"
            r="34"
            fill="none"
            stroke="var(--scribane-green)"
            strokeWidth="2"
          />
        </g>
        <g
          style={{
            ...NOTES_SHORTCUT_ANIMATION_STYLE,
            animation: 'notes-shortcut-ripple 3.4s ease-out infinite 0.18s',
          }}
        >
          <circle
            cx="74"
            cy="96"
            r="34"
            fill="none"
            stroke="var(--scribane-green)"
            strokeWidth="1.5"
            opacity="0.7"
          />
        </g>
        <g
          style={{
            ...NOTES_SHORTCUT_ANIMATION_STYLE,
            animation: 'notes-shortcut-press 3.4s ease-in-out infinite',
          }}
        >
          <rect
            x="50"
            y="72"
            width="48"
            height="48"
            rx="12"
            fill="var(--scribane-green)"
          />
          <image
            href={scribaneMarkUrl}
            x="62"
            y="76"
            width="24"
            height="40"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </g>
        <g
          style={{
            ...NOTES_SHORTCUT_ANIMATION_STYLE,
            animation: 'notes-shortcut-touch 3.4s ease-in-out infinite',
          }}
        >
          <circle cx="74" cy="96" r="7" fill="var(--foreground)" opacity="0.18" />
          <circle cx="74" cy="96" r="3.5" fill="var(--foreground)" opacity="0.45" />
        </g>
        <g
          style={{
            ...NOTES_SHORTCUT_ANIMATION_STYLE,
            transformOrigin: '50% 10%',
            animation: 'notes-shortcut-finger 3.4s ease-in-out infinite',
          }}
        >
          <path
            d="M90 120 C104 116 114 126 110 138 C107 147 96 148 88 140 C84 134 84 126 90 120 Z"
            fill="var(--card)"
            stroke="var(--border)"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M70 108 C66 99 69 90 77 88 C86 86 92 93 91 102 C98 108 103 122 101 138 C99 151 93 161 84 164 C74 167 64 162 63 151 C61 138 65 121 70 110 Z"
            fill="var(--card)"
            stroke="var(--border)"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <ellipse
            cx="78"
            cy="94"
            rx="6"
            ry="5"
            fill="var(--muted)"
            stroke="var(--border)"
            strokeWidth="1.2"
          />
        </g>
        <g
          style={{
            ...NOTES_SHORTCUT_ANIMATION_STYLE,
            animation: 'notes-shortcut-menu 3.4s ease-in-out infinite',
          }}
        >
          <rect
            x="118"
            y="46"
            width="86"
            height="78"
            rx="10"
            fill="var(--card)"
            stroke="var(--border)"
            strokeWidth="2"
          />
          <rect x="128" y="56" width="66" height="14" rx="4" fill="var(--muted)" />
          <g
            style={{
              ...NOTES_SHORTCUT_ANIMATION_STYLE,
              animation: 'notes-shortcut-item 3.4s ease-in-out infinite',
            }}
          >
            <rect
              x="128"
              y="76"
              width="66"
              height="22"
              rx="5"
              fill="var(--scribane-green)"
              opacity="0.18"
              stroke="var(--scribane-green)"
              strokeWidth="1.5"
            />
            <line x1="138" y1="87" x2="146" y2="87" stroke="var(--scribane-green)" strokeWidth="2" strokeLinecap="round" />
            <line x1="142" y1="83" x2="142" y2="91" stroke="var(--scribane-green)" strokeWidth="2" strokeLinecap="round" />
            <line x1="152" y1="84" x2="184" y2="84" stroke="var(--scribane-green)" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="152" y1="90" x2="176" y2="90" stroke="var(--scribane-green)" strokeWidth="1.8" strokeLinecap="round" opacity="0.7" />
          </g>
          <rect x="128" y="104" width="50" height="10" rx="4" fill="var(--muted)" opacity="0.7" />
        </g>
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};

export const CharactersEmptyIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={58} />
        <rect x="52" y="138" width="136" height="14" rx="7" fill="var(--muted)" stroke="var(--border)" strokeWidth="1.5" />
        <EmptyIllustrationAvatar cx={56} cy={78} variant="a" tone="muted" />
        <EmptyIllustrationAvatar cx={120} cy={48} variant="b" tone="filled" />
        <EmptyIllustrationAvatar cx={184} cy={72} variant="c" tone="outline-green" />
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};

export const LocationsEmptyIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={54} />
        <rect x="62" y="108" width="116" height="48" rx="8" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
        <path d="M78 128 H162 M78 140 H148 M90 116 V148 M120 116 V148 M150 116 V148" stroke="var(--border)" strokeWidth="1.2" opacity="0.45" />
        <path d="M120 122 L126 138 H114 Z" fill="var(--scribane-green)" />
        <circle cx="120" cy="118" r="5" fill="var(--scribane-green)" />
        <EmptyIllustrationMiniCard x={28} y={42} variant="a" tone="muted" />
        <EmptyIllustrationMiniCard x={104} y={14} variant="b" tone="filled" width={34} />
        <EmptyIllustrationMiniCard x={176} y={38} variant="c" tone="outline-green" />
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};

export const ScenesEmptyIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={52} />
        <line x1="48" y1="128" x2="192" y2="128" stroke="var(--border)" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="64" y1="128" x2="64" y2="86" stroke="var(--border)" strokeWidth="2" strokeLinecap="round" />
        <line x1="120" y1="128" x2="120" y2="54" stroke="var(--scribane-green)" strokeWidth="2" strokeLinecap="round" />
        <line x1="176" y1="128" x2="176" y2="78" stroke="var(--muted-foreground)" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
        <rect x="56" y="124" width="16" height="8" rx="2" fill="var(--card)" stroke="var(--border)" strokeWidth="1.5" />
        <rect x="112" y="124" width="16" height="8" rx="2" fill="var(--scribane-green)" />
        <rect x="168" y="124" width="16" height="8" rx="2" fill="var(--card)" stroke="var(--border)" strokeWidth="1.5" />
        <EmptyIllustrationMiniCard x={48} y={56} variant="a" tone="muted" />
        <EmptyIllustrationMiniCard x={103} y={24} variant="b" tone="filled" width={34} />
        <EmptyIllustrationMiniCard x={160} y={48} variant="c" tone="outline-green" />
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};

export const ChaptersEmptyIllustration: React.FC = () => {
  return (
    <EmptyIllustrationFrame>
      <EmptyIllustrationSvg>
        <EmptyIllustrationGroundShadow rx={48} />
        <rect x="86" y="108" width="68" height="48" rx="6" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
        <line x1="120" y1="108" x2="120" y2="156" stroke="var(--border)" strokeWidth="1.5" />
        <line x1="96" y1="120" x2="112" y2="120" stroke="var(--border)" strokeWidth="1.5" opacity="0.6" />
        <line x1="96" y1="128" x2="108" y2="128" stroke="var(--border)" strokeWidth="1.5" opacity="0.6" />
        <line x1="128" y1="120" x2="144" y2="120" stroke="var(--border)" strokeWidth="1.5" opacity="0.6" />
        <line x1="128" y1="128" x2="140" y2="128" stroke="var(--border)" strokeWidth="1.5" opacity="0.6" />
        <g style={emptyIllustrationSvgFloatStyle('a')}>
          <rect x="36" y="44" width="36" height="28" rx="6" fill="var(--card)" stroke="var(--border)" strokeWidth="2" />
          <text x="54" y="63" textAnchor="middle" fill="var(--muted-foreground)" fontSize="14" fontWeight="600">1</text>
        </g>
        <g style={emptyIllustrationSvgFloatStyle('b')}>
          <rect x={102} y={16} width="36" height="28" rx="6" fill="var(--scribane-green)" />
          <text x="120" y="35" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">2</text>
        </g>
        <g style={emptyIllustrationSvgFloatStyle('c')}>
          <rect x="168" y="40" width="36" height="28" rx="6" fill="var(--card)" stroke="var(--scribane-green)" strokeWidth="2" />
          <text x="186" y="59" textAnchor="middle" fill="var(--scribane-green)" fontSize="14" fontWeight="600">3</text>
        </g>
      </EmptyIllustrationSvg>
    </EmptyIllustrationFrame>
  );
};
